import styled from "@emotion/styled";
import { truncate } from "es-toolkit/compat";
import type { ReactNode } from "react";

import {
  Button,
  ConfirmDestructiveButton,
  FONT_WEIGHT_SEMI_BOLD,
  IconButton,
  InfoBanner,
  Popover,
  Tooltip,
  zIndexes,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

type Props = {
  children?: ReactNode;
  id: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onRemove: () => void;
  title: string;
  description?: string;
  conditionOrEffect: "condition" | "effect";
};

export const EditPopover = ({
  children,
  id,
  anchorEl,
  onClose,
  onRemove,
  title,
  description,
  conditionOrEffect,
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const isPopoverOpen = Boolean(anchorEl);

  return (
    <StyledPopover
      anchorEl={anchorEl}
      id={id}
      onClose={onClose}
      open={isPopoverOpen}
      slotProps={{
        paper: {
          className: `condition-popover-paper`,
        },
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
    >
      <PopoverHeader>
        <Tooltip
          title={title}
          slotProps={{ popper: { style: { zIndex: zIndexes.triggersOverlayPopover + 1 } } }}
          placement="top"
        >
          <h4>{truncate(title, { length: 125 })}</h4>
        </Tooltip>
        <IconButton icon="times" onClick={onClose} data-test="close-popover-button" />
      </PopoverHeader>
      <PopoverContent>
        {description && (
          <DescriptionContainer>
            <InfoBanner>{description}</InfoBanner>
          </DescriptionContainer>
        )}
        {children}
      </PopoverContent>
      <PopoverFooter>
        <ConfirmDestructiveButton
          label={formatMessage("remove")}
          header={formatMessage("deleteHeader", {
            conditionOrEffect: conditionOrEffect === "condition" ? "condition" : "action",
          })}
          message={formatMessage("deleteContent", {
            conditionOrEffect: conditionOrEffect === "condition" ? "condition" : "action",
          })}
          onDestroy={onRemove}
          color="error"
          size="small"
          enabledKeyboardShortcut
        />
        <Button variant="contained" onClick={onClose} size="small">
          {formatMessage("done")}
        </Button>
      </PopoverFooter>
    </StyledPopover>
  );
};

const StyledPopover = styled(Popover)`
  z-index: ${zIndexes.triggersOverlayPopover};

  .condition-popover-paper {
    border-radius: 5px;
    min-width: 320px;
    max-width: 420px;
  }
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.surfaceVariant};
  padding: 5px;

  > h4 {
    font-weight: ${FONT_WEIGHT_SEMI_BOLD};
    margin: 0;
    padding: 5px 10px;
  }
`;

const PopoverContent = styled.div`
  background: ${({ theme }) => theme.surface};
  padding: 15px;
`;

const DescriptionContainer = styled.div`
  margin-bottom: 10px;
  max-width: 300px;
`;

const PopoverFooter = styled.div`
  display: flex;
  justify-content: space-between;
  border-top: solid 1px ${({ theme }) => theme.outlineSoft};
  background: ${({ theme }) => theme.surface};
  padding: 10px 15px;
`;

const formatStrings = {
  deleteHeader: {
    en: "Delete {conditionOrEffect}",
  },
  deleteContent: {
    en: `Are you sure you want to delete this {conditionOrEffect}? This cannot be undone.`,
  },
  remove: {
    en: "Remove",
  },
  done: {
    en: "Done",
  },
};
